import React, { useState, useEffect } from 'react';
import { Snackbar, Box, IconButton, Typography, Button, Popover, TextField, MenuItem, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArticleIcon from '@mui/icons-material/Article';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import { GetPolicyData } from '../../../Api/Policy';
import { STATUS_OPTIONS } from '../../../Constants/Constants';
import { handleFileUpload, handleFileDelete, handleFileDownload } from '../../../Utils/fileHandlers';
import { toast } from 'react-toastify';
import { addFrameworkControlOrganization } from '../../../Api/Control';
import { validationConfigs } from '../../../Utils/fileValidation';
import StandardInputField from '../StandardInputField/StandardInputField';

const StandardSnackbar = ({ open, selectedRows = [], onClose, getAllData }) => {
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [policyAnchorEl, setPolicyAnchorEl] = useState(null);
  const [remarksAnchorEl, setRemarksAnchorEl] = useState(null);
  const [uploadAnchorEl, setUploadAnchorEl] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const OrganizationId = localStorage.getItem("organizationId");
  const getAccountID = localStorage.getItem("accountId");
  const [policynames, setPolicynames] = useState([]);
  const [bulkUpdateValues, setBulkUpdateValues] = useState({
    status: "",
    policy: [],
    remarks: "",
    evidence: []
  });

  useEffect(() => {
    GetPolicyAllData();
  }, []);

  const GetPolicyAllData = async () => {
    try {
      setLoading(true);
      const response = await GetPolicyData(OrganizationId, null, null); // null for search and type to show all types
      const rowsData = response.status === 200 ? response.data : [];
      if (Array.isArray(rowsData)) {
        setPolicynames(rowsData);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handlePolicyClick = (event) => {
    setPolicyAnchorEl(event.currentTarget);
  };

  const handlePolicyClose = () => {
    setPolicyAnchorEl(null);
  };

  const handleRemarksClick = (event) => {
    setRemarksAnchorEl(event.currentTarget);
  };

  const handleRemarksClose = () => {
    setRemarksAnchorEl(null);
  };

  const handleUploadClick = (event) => {
    setUploadAnchorEl(event.currentTarget);
  };

  const handleUploadClose = () => {
    setUploadAnchorEl(null);
  };

  const handleUploadEvidence = async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (uploadedFiles.length + files.length > 5) {
      toast.error("Maximum 5 files can be uploaded");
      event.target.value = "";
      return;
    }
    setLoading(true);
    try {
      for (const file of files) {
        const existingFile = uploadedFiles.find(f => f.name === file.name);
        if (existingFile) {
          toast.error(`The file "${file.name}" has already been uploaded.`);
          continue;
        }
        const fileData = await handleFileUpload(file, {
          relatedModelType: "control",
          createdBy: getAccountID,
          validationConfig: validationConfigs.control,
          onSuccess: (data) => {},
          onError: (error) => {
            toast.error(`Failed to upload "${file.name}": ${error.message}`);
          }
        });
        if (fileData) {
          const newFile = {
            id: fileData.id,
            upload_id: fileData.uploadId,
            name: fileData.fileName,
            size: fileData.fileSize,
            url: fileData.url
          };
          setUploadedFiles(prev => [...prev, newFile]);
          setBulkUpdateValues(prev => ({ 
            ...prev, 
            evidence: [...prev.evidence, newFile]
          }));
        }
      }
    } catch (error) {
      toast.error("Error uploading files");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const handleDownload = async (file) => {
    try {
      await handleFileDownload(file);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (file) => {
    try {
      setLoading(true);
      await handleFileDelete(
        { id: file.id, uploadId: file.upload_id },
        () => {
          setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
        },
        (error) => {
          toast.error(`Failed to delete "${file.name}": ${error.message}`);
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async (field, value) => {
    try {
      setLoading(true);
      await Promise.all(selectedRows.map(row => {
        const existingEvidence = row.framework_control_organization?.[0] || {};
        const existingFileIds = existingEvidence.fco_file_id ? 
          (Array.isArray(existingEvidence.fco_file_id) ? existingEvidence.fco_file_id : [existingEvidence.fco_file_id]) : 
          [];
        
        // Handle policy_id as array for bulk operations
        let policyId = null;
        if (Array.isArray(bulkUpdateValues.policy) && bulkUpdateValues.policy.length > 0) {
          // If there are selected policies in the form, use them
          policyId = bulkUpdateValues.policy.map(p => p.id);
        } else if (existingEvidence.fco_policy_id) {
          // Otherwise, use existing policy, ensure it's in array format
          policyId = Array.isArray(existingEvidence.fco_policy_id) 
            ? existingEvidence.fco_policy_id 
            : [existingEvidence.fco_policy_id];
        }
        
        const payload = {
          control_id: row.id,
          status: bulkUpdateValues.status || existingEvidence.fco_status || "Need Action",
          group_id: row.group_id || null,
          remark: bulkUpdateValues.remarks || existingEvidence.fco_remark || "",
          policy_id: policyId,
          file_id: bulkUpdateValues.evidence.length ? [...existingFileIds, ...bulkUpdateValues.evidence.map(f => f.id)] : existingFileIds,
          organization_id: OrganizationId,
          created_by: getAccountID,
          updated_by: getAccountID,
        };
        return addFrameworkControlOrganization(payload);
      }));

      if (getAllData) {
        await getAllData();
      }

      switch(field) {
        case 'status': handleStatusClose(); break;
        case 'policy': handlePolicyClose(); break;
        case 'remarks': handleRemarksClose(); break;
        case 'evidence': handleUploadClose(); break;
      }

      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} Updated Successfully for ${selectedRows.length} Control${selectedRows.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Update failed');
    } finally {
      setLoading(false);
    }
  };

  const popoverStyles = {
    position: 'relative',
    width: '360px',
    p: 4,
    pr: 6,
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
    border: '1px solid #E5E7EB'
  };

  const closeButtonStyles = {
    position: 'absolute',
    right: 12,
    top: 12,
    color: '#6B7280',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    width: '28px',
    height: '28px',
    '&:hover': {
      backgroundColor: '#F3F4F6',
      color: '#374151'
    }
  };

  const setStatusValue = (value) => {
    setBulkUpdateValues(prev => ({ ...prev, status: value }));
  };

  const setPolicyValue = (value) => {
    const selectedPolicies = policynames
      .filter(policy => value.includes(policy.id))
      .map(policy => ({
        id: policy.id,
        name: policy.name,
        version: policy.fco_policy_version || policy.version || 1
      }));
    setBulkUpdateValues(prev => ({ ...prev, policy: selectedPolicies }));
  };

  const setRemarksValue = (value) => {
    setBulkUpdateValues(prev => ({ ...prev, remarks: value }));
  };

  useEffect(() => {
    setBulkUpdateValues(prev => ({ ...prev, evidence: uploadedFiles }));
  }, [uploadedFiles]);

  const clearAllValues = () => {
    setBulkUpdateValues({
      status: "",
      policy: [],
      remarks: "",
      evidence: []
    });
    setUploadedFiles([]);
  };

  const handleSnackbarClose = () => {
    clearAllValues();
    onClose();
  };

  return (
    <>
      <Snackbar
        open={open}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#FFFFFF',
            color: '#000',
            padding: '0px',
            minWidth: '90vw',
            maxWidth: '95vw',
            borderRadius: '12px',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid #E5E7EB',
            '& .MuiSnackbarContent-message': {
              width: '100%',
              padding: 0
            }
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={
          <Box sx={{ width: '100%' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: '100%', 
              padding: '16px 24px',
              borderBottom: '1px solid #F3F4F6'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <IconButton 
                  size="small" 
                  onClick={handleSnackbarClose}
                  sx={{ 
                    backgroundColor: '#F9FAFB', 
                    borderRadius: '8px', 
                    border: '1px solid #E5E7EB',
                    width: '32px',
                    height: '32px',
                    '&:hover': { 
                      backgroundColor: '#F3F4F6',
                      borderColor: '#D1D5DB'
                    } 
                  }}
                >
                  <CloseIcon fontSize="small" sx={{ color: '#6B7280' }} />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#3B82F6'
                  }} />
                  <Typography sx={{ 
                    fontSize: '16px', 
                    fontWeight: 600, 
                    color: '#111827'
                  }}>
                    {`${selectedRows.length} Control${selectedRows.length === 1 ? '' : 's'} Selected`}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                <IconButton 
                  size="small" 
                  onClick={handleStatusClick} 
                  sx={{ 
                    backgroundColor: '#F8FAFC', 
                    borderRadius: '8px', 
                    border: '1px solid #E2E8F0',
                    padding: '8px 16px',
                    height: '40px',
                    minWidth: '140px',
                    '&:hover': { 
                      backgroundColor: '#F1F5F9',
                      borderColor: '#CBD5E1',
                      transform: 'translateY(-1px)',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutorenewIcon sx={{ color: '#3B82F6', fontSize: '18px' }} />
                    <Typography sx={{ 
                      color: '#3B82F6', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      textTransform: 'none'
                    }}>
                      Change Status
                    </Typography>
                    <ArrowDropDownIcon sx={{ color: '#3B82F6', fontSize: '18px' }} />
                  </Box>
                </IconButton>

                <IconButton 
                  size="small" 
                  onClick={handleRemarksClick} 
                  sx={{ 
                    backgroundColor: '#F8FAFC', 
                    borderRadius: '8px', 
                    border: '1px solid #E2E8F0',
                    padding: '8px 16px',
                    height: '40px',
                    minWidth: '140px',
                    '&:hover': { 
                      backgroundColor: '#F1F5F9',
                      borderColor: '#CBD5E1',
                      transform: 'translateY(-1px)',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditNoteIcon sx={{ color: '#10B981', fontSize: '18px' }} />
                    <Typography sx={{ 
                      color: '#10B981', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      textTransform: 'none'
                    }}>
                      Change Remark
                    </Typography>
                  </Box>
                </IconButton>

                <IconButton 
                  size="small" 
                  onClick={handlePolicyClick} 
                  sx={{ 
                    backgroundColor: '#F8FAFC', 
                    borderRadius: '8px', 
                    border: '1px solid #E2E8F0',
                    padding: '8px 16px',
                    height: '40px',
                    minWidth: '160px',
                    '&:hover': { 
                      backgroundColor: '#F1F5F9',
                      borderColor: '#CBD5E1',
                      transform: 'translateY(-1px)',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArticleIcon sx={{ color: '#8B5CF6', fontSize: '18px' }} />
                    <Typography sx={{ 
                      color: '#8B5CF6', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      textTransform: 'none'
                    }}>
                      Update Policies
                    </Typography>
                    <ArrowDropDownIcon sx={{ color: '#8B5CF6', fontSize: '18px' }} />
                  </Box>
                </IconButton>

                <IconButton 
                  size="small" 
                  onClick={handleUploadClick} 
                  sx={{ 
                    backgroundColor: '#F8FAFC', 
                    borderRadius: '8px', 
                    border: '1px solid #E2E8F0',
                    padding: '8px 16px',
                    height: '40px',
                    minWidth: '200px',
                    '&:hover': { 
                      backgroundColor: '#F1F5F9',
                      borderColor: '#CBD5E1',
                      transform: 'translateY(-1px)',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileUploadIcon sx={{ color: '#F59E0B', fontSize: '18px' }} />
                    <Typography sx={{ 
                      color: '#F59E0B', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      textTransform: 'none'
                    }}>
                      Upload New Set of Evidence
                    </Typography>
                  </Box>
                </IconButton>
              </Box>
            </Box>
          </Box>
        }
      />
      <Popover
        open={Boolean(statusAnchorEl)}
        anchorEl={statusAnchorEl}
        onClose={handleStatusClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={popoverStyles}>
          <IconButton onClick={handleStatusClose} sx={closeButtonStyles}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <TextField
            select
            fullWidth
            label="Status"
            value={bulkUpdateValues.status}
            onChange={(e) => setStatusValue(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="" disabled>
              <em>Select Status</em>
            </MenuItem>
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleBulkUpdate('status', bulkUpdateValues.status)}
            disabled={!bulkUpdateValues.status}
            sx={{ 
              backgroundColor: '#3B82F6', 
              borderRadius: '8px',
              height: '44px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 4px 12px rgba(59, 130, 246, 0.3)',
              '&:hover': { 
                backgroundColor: '#2563EB',
                boxShadow: '0px 6px 16px rgba(59, 130, 246, 0.4)'
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
                boxShadow: 'none'
              }
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Popover>

      <Popover
        open={Boolean(policyAnchorEl)}
        anchorEl={policyAnchorEl}
        onClose={handlePolicyClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={popoverStyles}>
          <IconButton onClick={handlePolicyClose} sx={closeButtonStyles}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <TextField
            select
            fullWidth
            label="Policy"
            value={bulkUpdateValues.policy.map(p => p.id)}
            onChange={(e) => setPolicyValue(e.target.value)}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {bulkUpdateValues.policy.map((policy) => (
                    <Chip 
                      key={policy.id} 
                      label={`${policy.name} v${policy.version}`}
                      size="small"
                      icon={<DescriptionOutlinedIcon />}
                      sx={{
                        backgroundColor: '#EBEEFF',
                        color: '#1D3DFC',
                        fontWeight: '600',
                        fontSize: '10px',
                        height: '24px',
                        '& .MuiChip-icon': {
                          color: '#1D3DFC',
                          fontSize: '14px'
                        }
                      }}
                    />
                  ))}
                </Box>
              )
            }}
            sx={{ mb: 2 }}
          >
            <MenuItem value="" disabled>
              <em>Select Policies</em>
            </MenuItem>
            {policynames.filter((policy) => policy.is_archived !== true).map((policy) => (
              <MenuItem key={policy.id} value={policy.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', py: 1 }}>
                  <DescriptionOutlinedIcon sx={{ color: '#1D3DFC', fontSize: '18px' }} />
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    flex: 1
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#1D3DFC',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: 1.2
                    }}>
                      {policy.name}
                    </Typography>
                    <Chip 
                      label={`version ${policy.fco_policy_version || policy.version || 1}`}
                      size="small"
                      sx={{
                        backgroundColor: '#EBEEFF',
                        color: '#1D3DFC',
                        fontWeight: '600',
                        fontSize: '10px',
                        height: '18px',
                        maxWidth: 'fit-content',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  {Array.isArray(bulkUpdateValues.policy) && bulkUpdateValues.policy.some(p => p.id === policy.id) && (
                    <CheckIcon sx={{ color: '#1D3DFC', ml: 'auto' }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleBulkUpdate('policy', bulkUpdateValues.policy)}
            disabled={!bulkUpdateValues.policy.length}
            sx={{ 
              backgroundColor: '#8B5CF6', 
              borderRadius: '8px',
              height: '44px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 4px 12px rgba(139, 92, 246, 0.3)',
              '&:hover': { 
                backgroundColor: '#7C3AED',
                boxShadow: '0px 6px 16px rgba(139, 92, 246, 0.4)'
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
                boxShadow: 'none'
              }
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Popover>

      <Popover
        open={Boolean(remarksAnchorEl)}
        anchorEl={remarksAnchorEl}
        onClose={handleRemarksClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={popoverStyles}>
          <IconButton onClick={handleRemarksClose} sx={closeButtonStyles}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={4}
            value={bulkUpdateValues.remarks}
            onChange={(e) => setRemarksValue(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleBulkUpdate('remarks', bulkUpdateValues.remarks)}
            disabled={!bulkUpdateValues.remarks}
            sx={{ 
              backgroundColor: '#10B981', 
              borderRadius: '8px',
              height: '44px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 4px 12px rgba(16, 185, 129, 0.3)',
              '&:hover': { 
                backgroundColor: '#059669',
                boxShadow: '0px 6px 16px rgba(16, 185, 129, 0.4)'
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
                boxShadow: 'none'
              }
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Popover>

      <Popover
        open={Boolean(uploadAnchorEl)}
        anchorEl={uploadAnchorEl}
        onClose={handleUploadClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={popoverStyles}>
          <IconButton onClick={handleUploadClose} sx={closeButtonStyles}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <StandardInputField
            type="multifile"
            fileData={{ files: uploadedFiles }}
            fileHandlers={{
              onUpload: handleUploadEvidence,
              onDownload: handleDownload,
              onDelete: handleDelete
            }}
            validationConfig={{
              ...validationConfigs.control,
              initialExpanded: false
            }}
            config={{ 
              maxFiles: 5
            }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleBulkUpdate('evidence', uploadedFiles.map(file => file.id))}
            disabled={!uploadedFiles.length}
            sx={{ 
              backgroundColor: '#F59E0B', 
              borderRadius: '8px',
              height: '44px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 4px 12px rgba(245, 158, 11, 0.3)',
              '&:hover': { 
                backgroundColor: '#D97706',
                boxShadow: '0px 6px 16px rgba(245, 158, 11, 0.4)'
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
                boxShadow: 'none'
              },
              mt: 2
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default StandardSnackbar; 